import { useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { toast } from "@/components/ui/toast";
import { LOCAL_ONLY_NOTICE } from "@/lib/fixture-notice";
import { BACKUP_CHECKLIST, BACKUP_PREVIOUS, BACKUP_COMPLETE } from "@/data/mock";

/* RCV-01 — Backup Ceremony.
   Canonical source: prototype/pages/recovery/backup.html (no current
   Figma frame located). Two source-defined states — "Checklist" and
   "Complete" — both implemented as a manual state switcher, matching
   the SVC-04 Support Bundle precedent (Default/Generating) for a
   canonical page with exactly two fixed, non-parameterized states. */

export function BackupCeremony() {
  const [view, setView] = useState<"checklist" | "complete">("checklist");
  const steps = BACKUP_CHECKLIST;

  function beginStep2() {
    toast({ variant: "info", title: "Backup ceremony in progress — proceed in order", description: LOCAL_ONLY_NOTICE });
  }

  return (
    <div>
      <div className="mb-5 flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-xl font-extrabold text-ink">Backup Ceremony</h1>
          <p className="text-sm text-muted">Generate and distribute a new set of key shards to custodians.</p>
        </div>
        <div className="flex gap-1.5">
          <button
            type="button"
            onClick={() => setView("checklist")}
            className={`rounded-[6px] border px-3 py-1.5 text-xs font-semibold ${view === "checklist" ? "border-primary bg-primary text-on-primary" : "border-line bg-surface text-muted"}`}
          >
            Checklist
          </button>
          <button
            type="button"
            onClick={() => setView("complete")}
            className={`rounded-[6px] border px-3 py-1.5 text-xs font-semibold ${view === "complete" ? "border-primary bg-primary text-on-primary" : "border-line bg-surface text-muted"}`}
          >
            Complete
          </button>
        </div>
      </div>

      {view === "checklist" && (
        <div className="space-y-5">
          <div className="rounded-[10px] border border-line bg-surface p-6">
            <div className="mb-4 text-base font-bold text-ink">Backup Session BKP-2026-044</div>
            <ol className="space-y-3">
              {steps.map((s, i) => (
                <li key={s.label} className="flex items-start gap-3 rounded-md border border-line p-3">
                  <span
                    aria-hidden
                    className={`mt-0.5 grid size-5 shrink-0 place-items-center rounded-full text-[11px] font-bold ${s.done ? "bg-success text-white" : "border border-line-strong text-muted"}`}
                  >
                    {s.done ? "✓" : i + 1}
                  </span>
                  <div>
                    <div className="text-sm font-semibold text-ink">{s.label}</div>
                    <div className="text-xs text-muted">{s.detail}</div>
                  </div>
                </li>
              ))}
            </ol>
            <Button className="mt-4" onClick={beginStep2}>Begin Step 2</Button>
          </div>

          <div className="rounded-[10px] border border-line bg-surface p-6">
            <div className="mb-1 text-sm font-bold text-ink">Previous Backup</div>
            <div className="font-mono text-xs text-muted">{BACKUP_PREVIOUS}</div>
          </div>
        </div>
      )}

      {view === "complete" && (
        <div className="rounded-[10px] border border-line bg-surface p-8 text-center">
          <div className="mb-3 text-4xl">✓</div>
          <div className="mb-1 text-base font-bold text-ink">Backup Complete</div>
          <p className="mb-3 text-sm text-muted">Session {BACKUP_COMPLETE.id} completed successfully. All 5 shards distributed and receipts confirmed.</p>
          <div className="mb-5 font-mono text-xs text-muted">{BACKUP_COMPLETE.hash} · {BACKUP_COMPLETE.timestamp}</div>
          <Link to="/recovery/audit">
            <Button variant="secondary">View Audit Packet →</Button>
          </Link>
        </div>
      )}
    </div>
  );
}
